import type { ReactNode } from 'react';
import type { ResponsiveTableColumn } from './types';

function parsePx(value?: string): number {
  if (!value?.endsWith('px')) return 0;
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Desktop (table-fixed): ogni colonna ottiene una larghezza %.
 * Le colonne con `pct` vengono normalizzate a somma 100% — si ridimensionano
 * tutte insieme quando la finestra si restringe.
 */
export function normalizeColumnWidths<T>(
  columns: ResponsiveTableColumn<T>[],
): ResponsiveTableColumn<T>[] {
  const withPct = columns.map((col) => {
    if (col.pct != null) {
      return { ...col, width: `${col.pct}%` };
    }
    return col;
  });

  const missing = withPct.filter((c) => !c.width?.endsWith('%'));
  let result = withPct;

  if (missing.length > 0) {
    const pxTotal = withPct.reduce((sum, col) => {
      return sum + (parsePx(col.minWidth) || parsePx(col.width) || 112);
    }, 0);

    result = withPct.map((col) => {
      if (col.width?.endsWith('%')) return col;
      const px = parsePx(col.minWidth) || parsePx(col.width) || 112;
      const pct = (px / pxTotal) * 100;
      return { ...col, width: `${Math.round(pct * 100) / 100}%` };
    });
  }

  const pctTotal = result.reduce((sum, col) => {
    if (!col.width?.endsWith('%')) return sum;
    return sum + parseFloat(col.width);
  }, 0);

  if (pctTotal > 0 && Math.abs(pctTotal - 100) >= 0.01) {
    const factor = 100 / pctTotal;
    result = result.map((col) => {
      if (!col.width?.endsWith('%')) return col;
      const scaled = parseFloat(col.width) * factor;
      return { ...col, width: `${Math.round(scaled * 100) / 100}%` };
    });
  }

  return result;
}

/** Width for `<col>` — scroll usa px fissi, desktop usa % normalizzate. */
export function getColWidth<T>(
  col: ResponsiveTableColumn<T>,
  mode: 'full' | 'scroll',
): string | undefined {
  if (mode === 'full') {
    if (col.width?.endsWith('%')) return col.width;
    if (col.pct != null) return `${col.pct}%`;
    return undefined;
  }

  if (col.width?.endsWith('px')) return col.width;
  return col.minWidth ?? '112px';
}

/**
 * Shared column presets.
 * - `pct`: peso desktop (proporzionale, ridimensiona con la finestra)
 * - `width` / `minWidth`: px per layout scroll orizzontale (< lg)
 */
export const TABLE_COL = {
  checkbox: { pct: 3, width: '36px', minWidth: '36px', align: 'center' as const },
  actions: { pct: 4, width: '44px', minWidth: '44px', align: 'center' as const },
  status: { pct: 13, width: '148px', minWidth: '148px' },
  count: { pct: 9, width: '112px', minWidth: '112px', align: 'center' as const },
  score: { pct: 7, width: '64px', minWidth: '64px', align: 'center' as const },
  date: { pct: 10, width: '96px', minWidth: '96px' },
  channel: { pct: 10, width: '96px', minWidth: '96px' },
  interview: { pct: 11, width: '108px', minWidth: '108px' },
  compact: { pct: 8, width: '88px', minWidth: '88px' },
  title: { pct: 18, width: '180px', minWidth: '140px' },
  text: { pct: 12, width: '128px', minWidth: '108px' },
  subtitle: { pct: 14, width: '168px', minWidth: '148px' },
} as const;

/** Hairline between header, rows, and pagination footer. */
export const TABLE_DIVIDER_BORDER = 'border-border/40';

/** Shared header row — single divider under titles (no double line with row 1). */
export const TABLE_HEAD_ROW_CLASS = `bg-card border-b ${TABLE_DIVIDER_BORDER} [&_th]:bg-card`;

/** Body row separator — skip row 0; header border-b already divides. */
export const TABLE_BODY_ROW_BORDER_CLASS = `border-t ${TABLE_DIVIDER_BORDER}`;

/** Column titles — medium weight, muted (not semibold body text). */
export const TABLE_HEAD_CELL_CLASS =
  'font-medium text-muted-foreground text-sm align-middle';

export function headerLabelClass(truncate?: boolean): string {
  return truncate ? 'truncate block min-w-0' : 'whitespace-nowrap';
}

/** Resolve header content — call render fns at render time for live selection state. */
export function resolveRenderHeader<T>(col: ResponsiveTableColumn<T>): ReactNode {
  if (!col.renderHeader) return null;
  return typeof col.renderHeader === 'function' ? col.renderHeader() : col.renderHeader;
}

export function statusCellClass(): string {
  return 'inline-flex min-w-0 max-w-full';
}
